// Setup › Memory › Health: what the hourly quality watch measured (MEMORY-QUALITY-WATCH). Short
// labels, the detail behind (i). A measure that cannot be taken yet shows a dash and one quiet
// "Waiting" line, never a zero. The platform's own findings and thresholds live with the operator.
import { Badge, InfoTip, SettingsGroup } from "@companyos/ui";
import { formatDateTime } from "../format";
import type { AdminHealth } from "./memory-queries";

type Quality = AdminHealth["quality"];

const pct = (n: number | null) => (n === null ? "-" : `${Math.round(n * 100)}%`);
const num = (n: number | null) => (n === null ? "-" : n);

const WAITING_LABEL: Record<Quality["waiting"][number], string> = {
  excerpts: "Auto-saved share",
  why: "Saves with a why",
  brief: "Brief size",
  reads: "Reads per run",
  member_reads: "Reads by people",
};

function Metric({
  label,
  value,
  info,
  tone,
}: {
  label: string;
  value: number | string;
  info: string;
  tone?: "warn" | undefined;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5 rounded-lg border border-border bg-card px-3 py-2">
      <span className="flex items-center gap-1 text-xs text-muted-foreground">
        {label}
        <InfoTip label={`About ${label.toLowerCase()}`}>{info}</InfoTip>
      </span>
      <span
        className={`font-heading text-xl font-semibold tabular-nums ${tone === "warn" ? "text-warning" : ""}`}
      >
        {value}
      </span>
    </div>
  );
}

export function QualityBlock({ quality: q }: { quality: Quality }) {
  const c = q.company;
  return (
    <SettingsGroup
      title="Memory quality"
      actions={
        q.as_of ? (
          <span className="text-xs text-muted-foreground tabular-nums">
            Checked {formatDateTime(q.as_of)}
          </span>
        ) : undefined
      }
    >
      <div className="flex flex-col gap-3 px-4 py-3" data-slot="quality">
        {c === null ? (
          <p className="text-[13px] text-muted-foreground">
            Not measured yet. The first check runs within the hour.
          </p>
        ) : (
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <Metric
              label="Saves this hour"
              value={c.writes}
              info="Records agents saved in the last hour, all agents together."
            />
            <Metric
              label="Repeats"
              value={pct(c.duplicate_share)}
              tone={c.duplicate_share !== null && c.duplicate_share > 0.1 ? "warn" : undefined}
              info="The share of this hour's saves that say what an earlier save already said."
            />
            <Metric
              label="Auto-saved"
              value={pct(c.excerpt_share)}
              info="The share of records that are raw excerpts of chats, tasks and runs rather than a typed note, decision or lesson."
            />
            <Metric
              label="Saves with a why"
              value={pct(c.why_share)}
              info="The share of saves that say why they were worth keeping."
            />
            <Metric
              label="Brief size"
              value={c.brief_chars === null ? "-" : `${c.brief_chars.toLocaleString("en-US")}`}
              info="Characters of memory handed to an agent at the start of a run, on average."
            />
            <Metric
              label="Reads per run"
              value={num(c.reads_per_run)}
              info="How many times a run looked in memory, on average."
            />
            <Metric
              label="Old proposals"
              value={num(c.stale_proposals)}
              info="Proposals and lessons that have waited past the review window."
            />
            <Metric
              label="Conflicts open"
              value={num(c.contradictions_open)}
              info={
                c.oldest_contradiction_days === null
                  ? "Pairs of notes that disagree and have not been settled."
                  : `Pairs of notes that disagree and have not been settled. The oldest is ${c.oldest_contradiction_days} days old.`
              }
            />
            <Metric
              label="Out of date"
              value={num(q.facts.stale_records)}
              tone={(q.facts.stale_records ?? 0) > 0 ? "warn" : undefined}
              info={`Records that say something the company's people, seats and helpers no longer say. ${
                q.facts.checked_at ? `Last checked ${formatDateTime(q.facts.checked_at)}.` : ""
              } You get one archive card in the Memory lane to approve or keep.`}
            />
          </div>
        )}
        {q.writers.length ? (
          <table className="w-full text-[13px]" data-slot="quality-writers">
            <thead>
              <tr className="text-left text-xs text-faint">
                <th className="py-1.5 font-medium">Agent</th>
                <th className="px-2 py-1.5 text-right font-medium">Saves</th>
                <th className="px-2 py-1.5 text-right font-medium">Repeats</th>
                <th className="py-1.5 text-right font-medium">
                  <span className="inline-flex items-center gap-1">
                    Flood
                    <InfoTip label="About flood">
                      An agent saving far more than usual in an hour. The platform team is told.
                    </InfoTip>
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              {q.writers.map((x) => (
                <tr key={x.id} className="border-t border-subtle" data-writer={x.id}>
                  <td className="py-1.5">{x.name}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{x.writes}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{pct(x.duplicate_share)}</td>
                  <td className="py-1.5 text-right">
                    {x.flood ? <Badge variant="warning">Flood</Badge> : "-"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
        {q.spaces.length ? (
          <table className="w-full text-[13px]" data-slot="quality-spaces">
            <thead>
              <tr className="text-left text-xs text-faint">
                <th className="py-1.5 font-medium">Space</th>
                <th className="px-2 py-1.5 text-right font-medium">Saves</th>
                <th className="px-2 py-1.5 text-right font-medium">Repeats</th>
                <th className="px-2 py-1.5 text-right font-medium">Old proposals</th>
                <th className="py-1.5 text-right font-medium">Conflicts</th>
              </tr>
            </thead>
            <tbody>
              {q.spaces.map((s) => (
                <tr key={s.key} className="border-t border-subtle" data-quality-space={s.key}>
                  <td className="py-1.5">{s.name}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{s.writes}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{pct(s.duplicate_share)}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{num(s.stale_proposals)}</td>
                  <td className="py-1.5 text-right tabular-nums">{num(s.contradictions_open)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
        {q.waiting.length ? (
          <p
            className="flex flex-wrap items-center gap-1 text-xs text-muted-foreground"
            data-slot="quality-waiting"
          >
            Not measured yet: {q.waiting.map((k) => WAITING_LABEL[k]).join(", ")}
            <InfoTip label="About what is not measured yet">
              These need something the memory service or the agents' runs do not report yet. They
              show a dash, never a zero, and switch on by themselves.
            </InfoTip>
          </p>
        ) : null}
      </div>
    </SettingsGroup>
  );
}
